import { createContext, useReducer, useContext, ReactNode } from 'react';

enum DialogActionType {
  OPEN_DIALOG = 'OPEN_DIALOG',
  CLOSE_DIALOG = 'CLOSE_DIALOG',
}

enum CohortDialogType {
  CREATE_COHORT = 'createCohort',
  DELETE_COHORT = 'deleteCohort',
  RENAME_COHORT = 'renameCohort',
  EXPORT_COHORT = 'exportCohort',
  COPY_COHORT = 'copyCohort',
}

type CohortModalAction =
  | {
      type: DialogActionType.OPEN_DIALOG;
      payload: {
        dialogType: CohortDialogType;
        itemId: string | null;
        itemName: string | null;
        latestCohortVersionId: string | null;
      };
    }
  | { type: DialogActionType.CLOSE_DIALOG };

type CohortModalState = {
  activeDialog: CohortDialogType | null;
  itemId: string | null;
  itemName: string | null;
  latestCohortVersionId: string | null;
};

const initialCohortState: CohortModalState = {
  activeDialog: null,
  itemId: null,
  itemName: null,
  latestCohortVersionId: null,
};

const dialogReducer = (state: CohortModalState, action: CohortModalAction): CohortModalState => {
  switch (action.type) {
    case DialogActionType.OPEN_DIALOG:
      return {
        ...state,
        activeDialog: action.payload.dialogType,
        itemId: action.payload.itemId,
        itemName: action.payload.itemName,
        latestCohortVersionId: action.payload.latestCohortVersionId,
      };
    case DialogActionType.CLOSE_DIALOG:
      return { ...state, activeDialog: null };
    default:
      return state;
  }
};

const CohortModalContext = createContext<{
  state: CohortModalState;
  openDialog: (
    dialogType: CohortDialogType,
    itemId?: string | null,
    itemName?: string | null,
    latestCohortVersionId?: string | null
  ) => void;
  closeDialog: () => void;
}>({
  state: initialCohortState,
  openDialog: () => null,
  closeDialog: () => null,
});

const CohortModalProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(dialogReducer, initialCohortState);

  const openDialog = (
    dialogType: CohortDialogType,
    itemId: string | null = null,
    itemName: string | null = null,
    latestCohortVersionId: string | null = null
  ) => {
    dispatch({
      type: DialogActionType.OPEN_DIALOG,
      payload: { dialogType, itemId, itemName, latestCohortVersionId },
    });
  };

  const closeDialog = () => {
    dispatch({ type: DialogActionType.CLOSE_DIALOG });
  };

  return (
    <CohortModalContext.Provider value={{ state, openDialog, closeDialog }}>
      {children}
    </CohortModalContext.Provider>
  );
};

const useCohortModalState = () => {
  const { state } = useContext(CohortModalContext);

  return {
    ...state,
    isCreateCohortModalOpen: state.activeDialog === CohortDialogType.CREATE_COHORT,
    isRenameCohortModalOpen: state.activeDialog === CohortDialogType.RENAME_COHORT,
    isDeleteCohortModalOpen: state.activeDialog === CohortDialogType.DELETE_COHORT,
    isExportCohortModalOpen: state.activeDialog === CohortDialogType.EXPORT_COHORT,
    isCopyCohortModalOpen: state.activeDialog === CohortDialogType.COPY_COHORT,
  };
};

const useCohortModalDispatch = () => {
  const { openDialog, closeDialog } = useContext(CohortModalContext);

  const openCreateCohortModal = () => {
    openDialog(CohortDialogType.CREATE_COHORT);
  };

  const openDeleteCohortModal = (cohortProfileId: string) => {
    openDialog(CohortDialogType.DELETE_COHORT, cohortProfileId);
  };

  const openRenameCohortModal = (cohortProfileId: string) => {
    openDialog(CohortDialogType.RENAME_COHORT, cohortProfileId);
  };

  const openExportCohortModal = (cohortProfileId: string) => {
    openDialog(CohortDialogType.EXPORT_COHORT, cohortProfileId);
  };

  const openCopyCohortModal = (
    cohortProfileId: string,
    cohortName: string,
    latestCohortVersionId: string
  ) => {
    openDialog(CohortDialogType.COPY_COHORT, cohortProfileId, cohortName, latestCohortVersionId);
  };

  return {
    openCreateCohortModal,
    openDeleteCohortModal,
    openRenameCohortModal,
    openExportCohortModal,
    openCopyCohortModal,
    closeDialog,
  };
};

const useCohortModalContext = () => {
  const state = useCohortModalState();
  const cohortModalDispatch = useCohortModalDispatch();
  return { ...state, ...cohortModalDispatch };
};

export { CohortDialogType, CohortModalProvider, useCohortModalContext };
